/**
 * Toast helpers — simple pub/sub store.
 *
 * Rendered by <Toaster /> (packages/ui/src/components/toaster.tsx).
 * Call-site API: toast.success(title, description?)
 */

// ── Types ────────────────────────────────────────────────────────────────────

export type ToastVariant = 'default' | 'success' | 'error' | 'warning';

export interface Toast {
  id: string;
  title?: string;
  description?: string;
  variant?: ToastVariant;
  /** Duration in ms. 0 = persist until dismissed. Default: 4500 */
  duration?: number;
}

type ToastInput = Omit<Toast, 'id'>;

// ── Singleton store (pub/sub) ─────────────────────────────────────────────────

let _toasts: Toast[] = [];
const _listeners = new Set<() => void>();

function _notify() { _listeners.forEach((cb) => cb()); }

function _subscribe(cb: () => void) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

function _snapshot(): Toast[] { return _toasts; }

function _add(input: ToastInput): string {
  const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const item: Toast = { variant: 'default', duration: 4500, ...input, id };
  _toasts = [item, ..._toasts].slice(0, 6);
  _notify();
  const dur = item.duration ?? 0;
  if (dur > 0) setTimeout(() => _dismiss(id), dur);
  return id;
}

function _dismiss(id: string) {
  _toasts = _toasts.filter((t) => t.id !== id);
  _notify();
}

// ── Imperative API ────────────────────────────────────────────────────────────

/**
 * @example
 * toast.success('Saved!');
 * toast.error('Failed', err.message);
 */
export const toast = Object.assign(
  (input: ToastInput) => _add(input),
  {
    success: (title: string, description?: string) =>
      _add({ variant: 'success', title, description }),
    error: (title: string, description?: string) =>
      _add({ variant: 'error', title, description }),
    warning: (title: string, description?: string) =>
      _add({ variant: 'warning', title, description }),
    dismiss: (id: string) => _dismiss(id),
  },
);

// ── React hooks ───────────────────────────────────────────────────────────────

import { useCallback, useSyncExternalStore } from 'react';

export function useToastStore() {
  const toasts = useSyncExternalStore(_subscribe, _snapshot, () => _toasts);
  return { toasts, dismiss: _dismiss };
}

export function useToast() {
  const show = useCallback((input: ToastInput) => _add(input), []);
  return {
    toast: Object.assign(show, {
      success: (title: string, description?: string) =>
        _add({ variant: 'success', title, description }),
      error: (title: string, description?: string) =>
        _add({ variant: 'error', title, description }),
      warning: (title: string, description?: string) =>
        _add({ variant: 'warning', title, description }),
      dismiss: (id: string) => _dismiss(id),
    }),
  };
}
